import { useState, useEffect } from 'react';
import { BACKEND_URL } from '@/lib/env';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, Activity, Clock, CheckCircle } from 'lucide-react';
import { usePermissions } from '@/contexts/PermissionsContext';
import { fetchAgentUsageStats } from '@/lib/agentStudioApi';
import { fetchTicketSummaries } from '@/lib/ticketsApi';

const API = `${BACKEND_URL}/api`;

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

export default function Analytics() {
  const { hasPermission } = usePermissions();
  const canReadTickets = hasPermission('tickets', 'read');
  const [tickets, setTickets] = useState([]);
  const [agentUsage, setAgentUsage] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [canReadTickets]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Tickets charts: one GET /tickets (lean page).
      // Agent charts: one agent-usage call (same source as Agent Studio).
      // Do NOT fan out /agents/{ticket}/tasks or /discovery/{ticket} — those N+1
      // patterns (plus CORS OPTIONS) were tripping Dev2 WAF (1000 req / 5 min / IP).
      // Discovery compliance APIs are not on incidentsapi today (always 404).
      if (!canReadTickets) {
        setTickets([]);
        setAgentUsage([]);
        return;
      }

      const page = await fetchTicketSummaries(API, { limit: 50, offset: 0, includeTotal: false });
      setTickets(page.items);

      try {
        const usageData = await fetchAgentUsageStats('last_30_days');
        setAgentUsage(Array.isArray(usageData?.agents) ? usageData.agents : []);
      } catch (err) {
        console.error('Error fetching agent usage:', err);
        setAgentUsage([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="text-lg">Loading...</div></div>;
  }

  // Ticket Status Distribution
  const statusDistribution = tickets.reduce((acc, ticket) => {
    const status = ticket.status.replace('_', ' ');
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  const statusData = Object.keys(statusDistribution).map(key => ({
    name: key,
    value: statusDistribution[key]
  }));

  // Priority Distribution
  const priorityDistribution = tickets.reduce((acc, ticket) => {
    acc[ticket.priority] = (acc[ticket.priority] || 0) + 1;
    return acc;
  }, {});

  const priorityData = Object.keys(priorityDistribution).map(key => ({
    name: key,
    count: priorityDistribution[key]
  }));

  // Agent Performance — from agent-usage telemetry (last 30 days), not per-ticket tasks
  const agentData = agentUsage
    .filter((row) => (row.total_runs || 0) > 0)
    .map((row) => {
      const completed = row.completed || 0;
      const failed = row.failed || 0;
      const totalRuns = row.total_runs || 0;
      const finished = completed + failed;
      const successRate =
        finished > 0 ? parseFloat(((completed / finished) * 100).toFixed(1)) : 0;
      return {
        name: row.agent_id || 'unknown',
        completed,
        failed,
        total_runs: totalRuns,
        success_rate: successRate,
      };
    });

  const agentCompletedTotal = agentUsage.reduce((sum, row) => sum + (row.completed || 0), 0);
  const agentFailedTotal = agentUsage.reduce((sum, row) => sum + (row.failed || 0), 0);
  const agentFinishedTotal = agentCompletedTotal + agentFailedTotal;
  const agentSuccessRate =
    agentFinishedTotal > 0
      ? ((agentCompletedTotal / agentFinishedTotal) * 100).toFixed(1)
      : '0';

  // Tickets over time (mock data for trend)
  const ticketTrend = [
    { month: 'Jan', tickets: 45, completed: 38 },
    { month: 'Feb', tickets: 52, completed: 45 },
    { month: 'Mar', tickets: 48, completed: 42 },
    { month: 'Apr', tickets: 61, completed: 55 },
    { month: 'May', tickets: 55, completed: 50 },
    { month: 'Jun', tickets: tickets.length, completed: tickets.filter(t => t.status === 'completed').length }
  ];

  // SAP System Distribution
  const systemDistribution = tickets.reduce((acc, ticket) => {
    acc[ticket.sap_system] = (acc[ticket.sap_system] || 0) + 1;
    return acc;
  }, {});

  const systemData = Object.keys(systemDistribution).map(key => ({
    name: key,
    value: systemDistribution[key]
  }));

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Analytics & Reporting</h1>
        <p className="text-muted-foreground mt-2">Comprehensive insights and performance metrics</p>
      </div>

      {!canReadTickets && (
        <Card>
          <CardContent className="py-6 text-sm text-muted-foreground">
            Ticket-backed charts need <span className="font-medium text-foreground">tickets.read</span>.
            Your role can open Analytics, but ticket metrics are hidden until that scope is granted.
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Tickets</CardTitle>
            <Activity className="h-4 w-4 text-cp-indigo" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{tickets.length}</div>
            <p className="text-xs text-muted-foreground">+12% from last month</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completion Rate</CardTitle>
            <CheckCircle className="h-4 w-4 text-cp-ok" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {tickets.length > 0 ? ((tickets.filter(t => t.status === 'completed').length / tickets.length) * 100).toFixed(1) : 0}%
            </div>
            <p className="text-xs text-muted-foreground">+5% from last month</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg. Delivery Time</CardTitle>
            <Clock className="h-4 w-4 text-cp-mod" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">2.5 days</div>
            <p className="text-xs text-muted-foreground">-30% from last month</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Agent Success Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-cp-violet" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {agentSuccessRate}%
            </div>
            <p className="text-xs text-muted-foreground">Last 30 days (agent usage)</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="tickets" className="w-full">
        <TabsList>
          <TabsTrigger value="tickets">Tickets</TabsTrigger>
          <TabsTrigger value="agents">Agents</TabsTrigger>
          <TabsTrigger value="compliance">Compliance</TabsTrigger>
          <TabsTrigger value="systems">Systems</TabsTrigger>
        </TabsList>

        <TabsContent value="tickets" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Ticket Status Distribution</CardTitle>
                <CardDescription>Current status breakdown of all tickets</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={statusData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {statusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Priority Distribution</CardTitle>
                <CardDescription>Tickets by priority level</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={priorityData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#3b82f6" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Ticket Trend</CardTitle>
              <CardDescription>Monthly ticket creation and completion rates</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={ticketTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="tickets" stroke="#3b82f6" strokeWidth={2} name="Created" />
                  <Line type="monotone" dataKey="completed" stroke="#10b981" strokeWidth={2} name="Completed" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="agents" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Agent Performance</CardTitle>
              <CardDescription>Run outcomes by agent (last 30 days, from usage telemetry)</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={agentData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="completed" stackId="a" fill="#10b981" name="Completed" />
                  <Bar dataKey="failed" stackId="a" fill="#ef4444" name="Failed" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {agentData.length === 0 ? (
              <Card className="md:col-span-2 lg:col-span-3 xl:col-span-5">
                <CardContent className="py-6 text-sm text-muted-foreground">
                  No agent usage recorded for the last 30 days.
                </CardContent>
              </Card>
            ) : (
              agentData.map((agent) => {
                const total = agent.total_runs || 0;
                const completed = agent.completed || 0;
                const successRate =
                  total > 0
                    ? Number(agent.success_rate || (completed / total) * 100).toFixed(1)
                    : '0';

                return (
                  <Card key={agent.name}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">{agent.name}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{successRate}%</div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {completed}/{total} runs completed
                      </p>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </TabsContent>

        <TabsContent value="compliance" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Clean Core Compliance Trend</CardTitle>
              <CardDescription>
                Discovery scan APIs are not available on this backend yet. This tab no longer
                calls per-ticket discovery endpoints (those were all 404s and inflated WAF
                request counts).
              </CardDescription>
            </CardHeader>
            <CardContent className="py-10 text-sm text-muted-foreground">
              Compliance charts will return when a list/aggregate discovery API is wired.
              Ticket and agent tabs above still use live data.
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="systems" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>SAP System Distribution</CardTitle>
                <CardDescription>Tickets by SAP system type</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={systemData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {systemData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>System Statistics</CardTitle>
                <CardDescription>Breakdown by system</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {systemData.map((system, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="h-3 w-3 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                        <span className="text-sm font-medium">{system.name}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-sm text-muted-foreground">{system.value} tickets</span>
                        <span className="text-sm font-semibold">
                          {tickets.length > 0 ? ((system.value / tickets.length) * 100).toFixed(1) : 0}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
